import React, { useCallback, useEffect, useMemo, useState } from "react";
import PlanOrganizationPresenter from "./PlanOrganizationPresenter";
import { TableColumnsType } from "antd";
import { changeSortOrderAntd } from "components/atoms/table/AntdTable";
import { formatNumber } from "utils/functions";
import {
  BasePagination,
  CustomError,
  LEVEL_TYPE,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { baseQueryInfo } from "utils/queryUtil";
import * as mediaQuery from "components/MediaQuery";
import AccountInfoLabel from "components/atoms/text/labels/AccountInfoLabel";
import StorageBar from "components/blocks/storage/StorageBar";
import SuspendButton from "pages/subscription/organization/list/SuspendButton";
import {
  ProfileAccountInfo,
  SuspendedPayload,
} from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { UsageLayoutOutletContext } from "api/interfaces/subscription.interface";
import { useOutletContext } from "react-router-dom";
import {
  GetPlanEuResponse,
  PlanEuListDto,
} from "api/interfaces/planInterface.interface";
import { useQuery } from "react-query";
import { getPlanEndUser } from "api/planAPI";
import { UnderlineText } from "components/atoms/text/Text";
import useTableQueryParams from "hook/useTableQueryParams";
import { SuspendWrapper } from "pages/subscription/SubscriptionStyled";
import { getAccountTimeFormatDate } from "utils/timeUtil";
import useStatusMutation from "hook/useStatusMutation";
import { postUpdateSuspension } from "api/accountAPI";
import useStompDebouncedRefetch from "hook/useStompDebouncedRefetch";
import { suspendOptions } from "utils/options";
import useApiError from "hook/useApiError";
import { useQueryClient } from "react-query";
import DeactivateModal from "components/blocks/modal/deactivate/DeactivateModal";
import RBACWrapper from "components/blocks/function/RBACWrapper";

const initialQueryInfo = {
  ...baseQueryInfo,
  pageLimit: 10,
};
export default function PlanOrganizationContainer() {
  const [selectedValue, setSelectedValue] = useState<Omit<
    SuspendedPayload,
    "accountId"
  > | null>(null);
  const selectedOption = useMemo(() => {
    if (!selectedValue) return null;
    return suspendOptions.find(
      (option) => option.value === selectedValue.suspensionStatus
    );
  }, [selectedValue]);

  const { handleErrorNotify, lang, notifySuccess } = useApiError();

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const queryClient = useQueryClient();

  const [pageInfo, setPageInfo] = useState<Partial<BasePagination>>({});

  const {
    setButtons,
    onChangePage,
    onOpenAddCloudModal,
    setStatus,
    onHeaderCell,
  } = useOutletContext<UsageLayoutOutletContext>();

  const { searchParams } = useTableQueryParams();

  const queryInfo = useMemo<BasePagination>(() => {
    const get = (key: string) => searchParams.get(key);

    return {
      ...initialQueryInfo,
      ...pageInfo,
      ...(get("pageNum") && { pageNum: Number(get("pageNum")) }),
      ...(get("pageLimit") && { pageLimit: Number(get("pageLimit")) }),
      ...{ keyword: get("keyword") ?? "" },
      ...(get("sortType") && { sortType: get("sortType")! }),
      ...(get("sortDirection") && {
        sortDirection: get("sortDirection") as SORT_DIRECTION,
      }),
      ...(get("type") && { type: get("type") }),
    };
  }, [searchParams, pageInfo]);
  const isMobile = mediaQuery.useIsMobile();

  const {
    data: result,
    refetch,
    isFetching,
  } = useQuery(
    ["getPlanEndUser", selectedAccount.accountId, queryInfo],
    () =>
      getPlanEndUser({
        accountId: selectedAccount.accountId,
        payload: queryInfo,
      }),
    {
      enabled: !!selectedAccount.accountId,
      retry: 0,
      onSuccess: (res: GetPlanEuResponse) => {
        if (res.page) {
          setStatus({ totalTitle: "Total", totalValue: res.page.total });
          setPageInfo((page) => {
            return {
              ...page,
              ...res.page,
            };
          });
        }
      },
    }
  );

  const mutationUpdateSuspension = useStatusMutation(postUpdateSuspension, {
    onSuccess: (res: any) => {
      notifySuccess(lang.label.billing.notify.update.success);
      setSelectedValue(null);
      ["getSubscriptonOrgList", "postPlanStatCard"].forEach((key) =>
        queryClient.invalidateQueries(key)
      );
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.billing.notify.update.fail);
    },
  });
  const onConfirmSuspend = useCallback(
    (targetAccountId: string, value: number) => {
      if (!mutationUpdateSuspension.idle) {
        return;
      }
      mutationUpdateSuspension.mutate({
        accountId: selectedAccount.accountId,
        targetAccountId: targetAccountId,
        suspensionStatus: value,
      });
    },
    [mutationUpdateSuspension, selectedAccount.accountId]
  );
  const onChangeStatus = useCallback(
    (payload: Omit<SuspendedPayload, "accountId">) => {
      setSelectedValue(payload);
    },
    []
  );

  const columns: TableColumnsType<PlanEuListDto> = useMemo(
    () => [
      {
        key: "name",
        title: "Organization Name",
        render: (value, row) => (
          <AccountInfoLabel
            level={LEVEL_TYPE.EU}
            accountNumber={row.accountNumber}
            accountName={row.name}
          />
        ),
        sorter: true,
        sortOrder: changeSortOrderAntd(queryInfo, "name"),
        defaultSortOrder: "ascend",
        onHeaderCell,
        ellipsis: true,
      },
      {
        title: "Channels",
        dataIndex: "cameras",
        render: (value) => formatNumber(value),
        width: 100,
      },
      {
        title: "Cloud Storage (TB)",
        dataIndex: "usedStorage",
        render: (value, row) => (
          <StorageBar totalStorage={row.totalStorage} usedStorage={value} />
        ),
        width: "20%",
      },
      {
        title: "Status",
        dataIndex: "isSuspended",
        hidden:
          selectedAccount.isHidden && !selectedAccount.userPermission.suspend,
        render: (value, row) => {
          return (
            <SuspendWrapper>
              <SuspendButton
                onChangeStatus={onChangeStatus}
                data={row}
                // isDisabled={!row.enableEdit}
              />
            </SuspendWrapper>
          );
        },
        width: isMobile ? 80 : 180,
        responsive: ["md"],
      },
      {
        title: "Last Activity",
        key: "activateDate",
        dataIndex: "activatedAt",
        render: (value, row) => {
          if (value) {
            return getAccountTimeFormatDate(value, selectedAccount, true, true);
          }
          return "-";
        },
        width: 160,
        sorter: true,
        sortOrder: changeSortOrderAntd(queryInfo, "activateDate"),
        onHeaderCell,
      },
      {
        title: "",
        render: (value, row) => (
          <RBACWrapper requiredPermissions="manageBillingAndLicenses">
            <UnderlineText
              fontSize={13}
              style={{ display: "inline" }}
              onClick={() => onOpenAddCloudModal(row)}
            >
              Add Cloud Storage
            </UnderlineText>
          </RBACWrapper>
        ),
        align: "right",
        width: 160,
      },
    ],
    [isMobile, onOpenAddCloudModal, queryInfo, refetch, selectedAccount]
  );
  useEffect(() => {
    setButtons(null);
  }, [setButtons]);

  const data: PlanEuListDto[] = useMemo(() => {
    if (result && result.error === 0) {
      return result.result;
    }
    return [];
  }, [result]);

  useStompDebouncedRefetch({
    selector: (state) =>
      ({
        organizationListUpdated: state.stomp.organizationListUpdated,
        subscriptionRequestUpdated:
          state.subscriptionStomp.subscriptionRequestUpdated,
        subscriptionConfirmUpdated:
          state.subscriptionStomp.subscriptionConfirmUpdated,
        subscriptionActivatedUpdated:
          state.subscriptionStomp.subscriptionActivatedUpdated,
        planAutoEnrollUpdated: state.subscriptionStomp.planAutoEnrollUpdated,
      } as Record<string, unknown>),
    stompKey: [
      "organizationListUpdated",
      "subscriptionRequestUpdated",
      "subscriptionConfirmUpdated",
      "subscriptionActivatedUpdated",
      "planAutoEnrollUpdated",
    ],

    refetch: refetch,
  });

  return (
    <>
      <PlanOrganizationPresenter
        columns={columns}
        data={data}
        onChangePage={onChangePage}
        isLoading={isFetching}
        queryInfo={queryInfo}
      />
      {selectedOption && selectedValue !== null && (
        <DeactivateModal
          title={selectedOption?.title ?? ""}
          subTitle={selectedOption?.subTitle ?? ""}
          onDeactive={() =>
            onConfirmSuspend(
              selectedValue.targetAccountId,
              selectedValue.suspensionStatus
            )
          }
          setIsShowModal={() => setSelectedValue(null)}
        />
      )}
    </>
  );
}
